'use client';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { toast } from 'sonner';
import { loginSchema, type LoginInput } from '@/lib/validators/auth.schema';
import { loginUser, AuthError } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';

// Stores only the email address (never the password) in this browser so the
// login field is pre-filled on the user's next visit from the same device.
const REMEMBERED_EMAIL_KEY = 'eifo_remembered_email';

export function LoginForm() {
  const t = useTranslations('auth');
  const tc = useTranslations('common');
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resendInFlight, setResendInFlight] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(false);

  const reason = searchParams.get('reason');
  const returnUrl = searchParams.get('returnUrl');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
  });

  // Pre-fill the email from a previous "remember me" login on this device.
  useEffect(() => {
    const saved = localStorage.getItem(REMEMBERED_EMAIL_KEY);
    if (saved) {
      setValue('email', saved);
      setRememberEmail(true);
    }
  }, [setValue]);

  const onSubmit = async (data: LoginInput) => {
    setIsSubmitting(true);
    setFormError(null);
    setUnverifiedEmail(null);

    // Persist (or clear) the remembered email before the redirect fires.
    if (rememberEmail) {
      localStorage.setItem(REMEMBERED_EMAIL_KEY, data.email);
    } else {
      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    }

    try {
      const result = await loginUser(data.email, data.password);

      // Redirect based on role or returnUrl
      const redirectUrl = returnUrl ?? getDefaultRedirect(result.user.role);
      router.push(redirectUrl);
    } catch (error) {
      if (error instanceof AuthError) {
        if (error.code === 'EMAIL_NOT_VERIFIED') {
          setUnverifiedEmail(data.email);
          return;
        }
        if (error.code === 'INVALID_CREDENTIALS' || error.code === 'ACCOUNT_DISABLED') {
          setFormError(error.message);
        } else {
          toast.error(error.message, { duration: 8000 });
        }
      } else {
        toast.error(t('login.errorGeneric'), {
          duration: 8000,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  async function handleResendVerification() {
    if (!unverifiedEmail || resendInFlight) return;
    setResendInFlight(true);
    try {
      await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: unverifiedEmail }),
      });
      toast.success(t('login.verificationSent'));
    } catch {
      toast.error(t('login.verificationFailed'));
    } finally {
      setResendInFlight(false);
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      {reason === 'session-expired' && (
        <div className="rounded-md border border-[#f59e0b]/30 bg-[#f59e0b]/10 p-3 text-sm text-[#f59e0b]">
          {t('login.sessionExpired')}
        </div>
      )}

      {reason === 'password-reset' && (
        <div className="rounded-md border border-[#22c55e]/30 bg-[#22c55e]/10 p-3 text-sm text-[#166534]">
          {t('login.passwordResetBanner')}
        </div>
      )}

      {formError && (
        <div className="rounded-md border border-[#ef4444]/30 bg-[#ef4444]/10 p-3 text-sm text-[#ef4444]">
          {formError}
        </div>
      )}

      {unverifiedEmail && (
        <div className="rounded-md border border-[#fc800a]/40 bg-[#fc800a]/10 p-3 text-sm text-[#7c2d12]">
          <p className="font-medium">
            {t('login.unverifiedTitle')}
          </p>
          <p className="mt-1 text-xs">
            {t('login.unverifiedBody')}
          </p>
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={resendInFlight}
            className="mt-2 rounded bg-[#fc800a] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#e07209] disabled:opacity-60"
          >
            {resendInFlight ? t('sending') : t('resendVerificationEmail')}
          </button>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm text-[#6b7280]">
          {tc('email')} <span className="text-[#ef4444]">*</span>
        </Label>
        <Input
          id="email"
          type="email"
          placeholder={t('fields.emailPlaceholder')}
          aria-describedby={errors.email ? 'email-error' : undefined}
          aria-invalid={!!errors.email}
          className="h-[44px] px-5 text-base focus-visible:ring-[#2fa5fb] focus-visible:ring-2 data-[invalid=true]:border-[#ef4444]"
          data-invalid={!!errors.email}
          {...register('email')}
        />
        {errors.email && (
          <p id="email-error" className="text-sm text-[#ef4444]">
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password" className="text-sm text-[#6b7280]">
          {t('fields.password')} <span className="text-[#ef4444]">*</span>
        </Label>
        <Input
          id="password"
          type="password"
          placeholder={t('fields.passwordPlaceholder')}
          aria-describedby={errors.password ? 'password-error' : undefined}
          aria-invalid={!!errors.password}
          className="h-[44px] px-5 text-base focus-visible:ring-[#2fa5fb] focus-visible:ring-2 data-[invalid=true]:border-[#ef4444]"
          data-invalid={!!errors.password}
          {...register('password')}
        />
        {errors.password && (
          <p id="password-error" className="text-sm text-[#ef4444]">
            {errors.password.message}
          </p>
        )}
        <div className="text-right">
          <Link
            href="/forgot-password"
            className="text-sm font-medium text-[#2fa5fb] hover:underline"
          >
            {t('login.forgotPassword')}
          </Link>
        </div>
      </div>

      <label
        htmlFor="rememberEmail"
        className="flex items-center gap-2.5 text-sm text-[#6b7280] cursor-pointer select-none"
      >
        <input
          id="rememberEmail"
          type="checkbox"
          checked={rememberEmail}
          onChange={(e) => setRememberEmail(e.target.checked)}
          className="h-4 w-4 rounded border-[#d1d5db] text-[#fc800a] focus:ring-[#fc800a]"
        />
        {t('login.rememberEmail')}
      </label>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="h-[44px] w-full bg-[#fc800a] text-white font-bold hover:bg-[#e07209] disabled:opacity-40 disabled:bg-[#fc800a]"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            <span className="sr-only">{t('login.submittingSr')}</span>
            {t('login.submitting')}
          </>
        ) : (
          t('login.submit')
        )}
      </Button>
    </form>
  );
}

function getDefaultRedirect(role: string): string {
  switch (role) {
    case 'admin':
      return '/admin';
    case 'teacher':
      return '/teacher';
    case 'parent':
      return '/dashboard';
    case 'student':
      return '/student/dashboard';
    default:
      return '/dashboard';
  }
}
